interface Props {
  lang: "en" | "ru";
}

const specsEn = [
  { label: "Engine Code", value: "K4M (1.6L, 16-Valve)" },
  { label: "Configuration", value: "Long Block (Bare Engine) / Short Block available upon request" },
  { label: "Block Material", value: "Heavy-duty Cast Iron Block" },
  { label: "Head Material", value: "Precision Aluminum Alloy Head" },
  { label: "Main Applications", value: "Renault Duster · Logan · Megane · Sandero · Lada Largus" },
  { label: "Displacement", value: "1,598 cc (1.6L)" },
  { label: "Valvetrain", value: "DOHC 16V" },
  { label: "Bore × Stroke", value: "79.5 mm × 80.5 mm" },
  { label: "Compression Ratio", value: "9.5:1" },
  { label: "Packaging", value: "Export-grade fumigation-free wooden crates (Palletized for FCL/LCL)" },
  { label: "MOQ", value: "Flexible — contact for B2B pricing" },
  { label: "Lead Time", value: "Quote within 24h · Delivery per logistics mode" },
];

const specsRu = [
  { label: "Модель двигателя", value: "K4M (1.6 л, 16 клапанов)" },
  { label: "Комплектация", value: "Long Block (двигатель в сборе без навесного) / Short Block по запросу" },
  { label: "Материал блока", value: "Чугунный блок цилиндров" },
  { label: "Материал ГБЦ", value: "Алюминиевая головка блока" },
  { label: "Применяемость", value: "Renault Duster · Logan · Megane · Sandero · Lada Largus" },
  { label: "Объём", value: "1 598 куб. см (1.6 л)" },
  { label: "Газораспределение", value: "DOHC 16V" },
  { label: "Диаметр × Ход поршня", value: "79.5 мм × 80.5 мм" },
  { label: "Степень сжатия", value: "9.5:1" },
  { label: "Упаковка", value: "Экспортные деревянные ящики (без фумигации), паллетирование" },
  { label: "МОЗ", value: "Гибкий — свяжитесь для получения B2B цены" },
  { label: "Срок поставки", value: "Прайс в течение 24ч · Доставка по выбранному маршруту" },
];

export default function K4MSpecs({ lang }: Props) {
  const isRu = lang === "ru";
  const specs = isRu ? specsRu : specsEn;

  return (
    <section className="py-24 px-6" style={{ background: "#F4F5F7" }}>
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* Left: Specs table */}
          <div>
            <p className="text-xs tracking-widest uppercase mb-4 font-medium" style={{ color: "#D97706", letterSpacing: "0.25em" }}>
              {isRu ? "Технические характеристики" : "Technical Specifications"}
            </p>
            <h2 className="text-3xl md:text-4xl font-thin mb-4" style={{ letterSpacing: "0.04em", color: "#1a2332" }}>
              {isRu ? "ТЕХНИЧЕСКИЕ ХАРАКТЕРИСТИКИ" : "TECHNICAL SPECIFICATIONS"}
              <br />
              <span style={{ color: "#D97706" }}>
                {isRu ? "Серия K4M (1.6L 16V)" : "K4M Series (1.6L 16V)"}
              </span>
            </h2>
            <div className="mb-8" style={{ width: "60px", height: "2px", background: "#F59E0B" }} />

            <div className="flex flex-col gap-0 rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(0,0,0,0.07)" }}>
              {specs.map((spec, i) => (
                <div
                  key={spec.label}
                  className="flex items-start gap-4 px-5 py-4"
                  style={{
                    background: i % 2 === 0 ? "#ffffff" : "#FAFAFA",
                    borderBottom: i < specs.length - 1 ? "1px solid rgba(0,0,0,0.05)" : "none",
                  }}
                >
                  <div className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: "#F59E0B" }} />
                  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 flex-1">
                    <span
                      className="text-xs font-semibold uppercase tracking-wide shrink-0"
                      style={{ color: "rgba(90,106,126,0.7)", letterSpacing: "0.08em", minWidth: "160px" }}
                    >
                      {spec.label}
                    </span>
                    <span className="text-sm font-medium" style={{ color: "#1a2332" }}>
                      {spec.value}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Engine image + application list */}
          <div>
            <div
              className="rounded-2xl overflow-hidden mb-8"
              style={{ height: "320px", border: "1px solid rgba(0,0,0,0.07)" }}
            >
              <img
                src="https://public.readdy.ai/ai/img_res/cc415311ff7f22a7be355fa0954bbdb2.jpg"
                alt="Brand new K4M engine for Duster Logan — Renault OEM replacement engine China wholesale"
                title="Renault K4M 1.6 16V Long Block — Guangzhou Auto Parts Export Hub"
                className="w-full h-full object-cover object-center"
              />
            </div>

            {/* Application vehicles */}
            <div
              className="rounded-2xl p-6 bg-white"
              style={{ border: "1px solid rgba(245,158,11,0.2)" }}
            >
              <p className="text-xs tracking-widest uppercase mb-4 font-medium" style={{ color: "#D97706", letterSpacing: "0.2em" }}>
                {isRu ? "Применяемость" : "Vehicle Applications"}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { model: "Renault Duster", years: "2010–2022" },
                  { model: "Renault Logan", years: "2004–2022" },
                  { model: "Renault Megane II", years: "2002–2009" },
                  { model: "Renault Sandero", years: "2008–2022" },
                  { model: "Lada Largus", years: "2012–present" },
                  { model: "Renault Scenic II", years: "2003–2009" },
                ].map((v) => (
                  <div
                    key={v.model}
                    className="flex items-start gap-3 p-3 rounded-xl"
                    style={{ background: "rgba(245,158,11,0.05)", border: "1px solid rgba(245,158,11,0.12)" }}
                  >
                    <i className="ri-car-line text-sm mt-0.5 shrink-0" style={{ color: "#D97706" }} />
                    <div>
                      <p className="text-xs font-semibold" style={{ color: "#1a2332" }}>{v.model}</p>
                      <p className="text-xs font-light" style={{ color: "rgba(90,106,126,0.6)", fontSize: "10px" }}>{v.years}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
